import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  useEditController,
  useNotify,
  Edit,
  FormTab,
  Loading,
  NumberInput,
  RadioButtonGroupInput,
  SaveButton,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  TopToolbar,
  required,
  number,
  minValue,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";

import {
  Backdrop,
  CircularProgress,
  Grid,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";

import {
  ResourceType,
  ServiceStatus,
  ServiceStatuses,
  ServiceType,
  UnitOptions,
} from "../../../utils/constants";
import { getUnitLabel } from "../../../utils/loadTypeUtils";
import {
  autogenHistoryNotes,
  deepRedirect,
  getBackPath,
  taxRateRenderer,
} from "../../../utils/utils";

import { theme } from "../../../themes";
import { logError, logHistory } from "../../../utils/appUtils";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { HelpButton } from "../../../components/common/HelpButton";
import { HistoryTab } from "../../../components/common/HistoryTab";
import { ServiceHelp } from "./ServiceHelp";
import { DeepDeleteButton } from "./DeepDeleteButton";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();
const validateRate = [number(), minValue(0)];

const EditToolbar = ({ basePath, backPath, isYardTemplate, ...props }) => {
  const classes = useStyles();
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const { pristine } = useFormState();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const transform = (data) => {
    data = { ...data };
    setIsSaving(true);

    debugger;
    // Log history for form changes
    autogenHistoryNotes(form, data.id, ResourceType.SERVICE);

    // Log specific tax rate change if applicable
    const taxState = form.getFieldState("taxRate");
    if (taxState?.dirty) {
      const historyNote = {
        parentId: data.id,
        parentType: ResourceType.SERVICE,
        text: `Tax Rate changed from ${taxState.initial.taxRate} to ${data.taxRate.taxRate}`,
      };
      logHistory(historyNote);
    }

    UnitOptions.forEach((unit) => {
      data[unit.column] = data[unit.column] ? parseFloat(data[unit.column]) : 0;
    });

    // History will be logged via autogenHistoryNotes
    delete data.history;
    delete data.historyLog;

    return data;
  };

  const onSuccess = () => {
    notify("Service updated successfully.");
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("services:ServiceEdit:onFailure:Service Update", error, notify);
  };

  return (
    <Toolbar style={{ justifyContent: "space-between" }}>
      <Backdrop className={classes.backdrop} open={isSaving || isDeleting}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <SaveButton
        {...props}
        label="Save"
        disabled={pristine || isDeleting}
        saving={isSaving}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <DeepDeleteButton
        {...props}
        backPath={backPath ? backPath : "/accounts"}
        isDeleting={isDeleting}
        setIsDeleting={setIsDeleting}
        isSaving={isSaving}
        isYardTemplate={isYardTemplate}
      />
    </Toolbar>
  );
};

const EditActions = ({ basePath, backPath, ...props }) => (
  <TopToolbar>
    <HelpButton>
      <ServiceHelp {...props} />
    </HelpButton>
    <BackButton basePath={"/accounts"} backPath={backPath} />
  </TopToolbar>
);

export const ServiceEdit = (props) => {
  const backPath = getBackPath(props);
  const { record, loading, loaded } = useEditController(props);
  const reloadYard = window.appUser?.reloadYard;
  const taxRateChoices = reloadYard?.taxRates ? reloadYard.taxRates : [{}];
  const isYardTemplate = reloadYard?.id === record?.accountId;

  if (loading || !loaded) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit Service" />}
      mutationMode="pessimistic"
      actions={<EditActions backPath={backPath} />}
    >
      <TabbedForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <EditToolbar
            {...props}
            backPath={backPath}
            isYardTemplate={isYardTemplate}
          />
        }
      >
        <FormTab label="Service Setup">
          <ServiceSetupTab
            {...props}
            record={record}
            taxRateChoices={taxRateChoices}
            isYardTemplate={isYardTemplate}
          />
        </FormTab>
        <FormTab label="History Log">
          <HistoryTab />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

const isServiceNmaeFixed = (record) => {
  return (
    record?.id && // used for create & edit
    record.name === ServiceType.ACCESSORIAL_CHARGES
  );
};

export const ServiceSetupTab = ({
  record,
  taxRateChoices,
  isYardTemplate,
  ...props
}) => {
  return (
    <Grid container direction="column" spacing={0}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={3}>
          <TextInput
            source="name"
            label="Service Name"
            variant="outlined"
            fullWidth
            validate={validateRequired}
            disabled={isServiceNmaeFixed(record)} // used for create & edit
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <TextInput
            source="description"
            label="Description"
            variant="outlined"
            fullWidth
            disabled={record.name === ServiceType.ACCESSORIAL_CHARGES}
          />
        </Grid>
      </Grid>
      {record.name !== ServiceType.ACCESSORIAL_CHARGES && (
        <Grid container direction="row" spacing={1}>
          {UnitOptions.map((unit) => (
            <Grid item xs={12} md={3}>
              <NumberInput
                source={unit.column}
                label={getUnitLabel(unit.id)}
                variant="outlined"
                fullWidth
                placeholder="0.00"
                min="0"
                format={(v) => (Number(v) === 0 ? undefined : v)}
                parse={(v) => (v === "0" ? undefined : v)}
                validate={validateRate}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"> $ </InputAdornment>
                  ),
                  inputMode: "decimal",
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={3}>
          <SelectInput
            source={"taxRate"}
            label="Tax Rate"
            variant="outlined"
            fullWidth
            choices={taxRateChoices}
            optionValue="taxCode"
            optionText={taxRateRenderer}
            format={(v) => (!v ? undefined : v.taxCode)}
            parse={(v) =>
              !v
                ? undefined
                : window.appUser.reloadYard.taxRates.find(
                    (r) => r.taxCode === v,
                  )
            }
            validate={validateRequired}
          />
        </Grid>
        {record?.id &&
          record.name !== ServiceType.ACCESSORIAL_CHARGES &&
          !isYardTemplate && (
            <Grid item xs={12} md={3}>
              <RadioButtonGroupInput
                source="status"
                label="Service Availability"
                style={{ marginTop: -theme.spacing(0.2) }}
                validate={validateRequired}
                defaultValue={ServiceStatus.AVAILABLE}
                choices={ServiceStatuses}
              />
            </Grid>
          )}
      </Grid>
    </Grid>
  );
};
